import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import "./css/home.scss";

import DocumentService from "../services/document_service";
import TextField from "../components/fields/text_field";
import { fake_state_todo_remove } from "../constants/fake_state_todo_remove";
import CurrencyField from "../components/fields/currency_field";
import { LOAN_TYPE_CATEGORY_NAMES } from "../constants/loan_types";
import DateField from "../components/fields/date_field";
import SelectField from "../components/fields/select_field";
import {
  APPROVED_DU,
  APPROVED_UW,
  CD_DISCLOSED,
  CLEARED_TO_CLOSE,
  CLOSED,
  DENIED,
  DENIED_COMPLIANCE,
  DENIED_COST_TO_CURE,
  DENIED_CREDIT,
  DENIED_QUAL_RATE,
  DENIED_RESERVES,
  DISCLOSE_LE,
  DISCLOSED_LE_NOT_SUBMITTED,
  DOCS_OUT,
  DPA_CLEARED,
  DPA_SUBMITTED,
  FUNDED,
  LOAN_STATUS_NAMES,
  LOAN_STATUS_ORDER,
  LOAN_STATUS_PERCENTS,
  LOST_NOT_PROCEEDING,
  LOST_TO_COMPETION,
  LOST_TO_PRICING,
  NEW,
  NOT_COMPLIANT,
  NOTES,
  ON_HOLD,
  PTD_COND,
  PTF_COND,
  SUBMITTED_TO_PROCESSOR,
  TBD_REPORT,
  WORKING_NOT_SUBMITTED
} from "../constants/loan_status";
import PercentField from "../components/fields/percent_field";
import { default_metadata } from "../schema/metadata";
import { default_pipeline_data } from "../schema/pipeline_data";
import DOCUMENT_TYPES, {
  PURCHASE,
  REFINANCE
} from "../constants/document_types";
import { class_list } from "../constants/utils";
import { isDashboardEnabled } from "../services/dashboard_service";
import moment from "moment-timezone";
import StandardTemplate from "../templates/standard_template/standard_template";
import { NO, YES, YES_NO } from "../constants/yes_no";
import { useUser } from "../hooks/user";
import { Link } from "@reach/router";
import TrainingTip from "../components/ui/training_tip";
import { fromFnmText } from "../fnm/fnm";
import { fromMismoXml } from "../mismo/mismo";
import {
  applyDebtImportFilter,
  countFilteredDebts
} from "../mismo/import_filters";
import CheckboxField from "../components/fields/checkbox_field";
import ErrorMessages from "../components/ui/error_messages";
import { registerPendingSaves } from "../services/pending_saves";
import { removeAccessToken } from "../services/utils";

// ---- Rate-lock / COE risk alerts (pipeline) ---------------------------------
// Thresholds in days. Amber = heads-up, red/orange = urgent. Configurable later.
const LOCK_WARN_DAYS = 7;
const LOCK_URGENT_DAYS = 3;
const COE_WARN_DAYS = 15;
const COE_URGENT_DAYS = 7;

// A decided/closed loan doesn't need a lock or COE alert — suppress for these.
const ALERT_SUPPRESS_STATUSES = [
  CLOSED,
  FUNDED,
  DENIED,
  DENIED_CREDIT,
  DENIED_COMPLIANCE,
  DENIED_COST_TO_CURE,
  DENIED_QUAL_RATE,
  DENIED_RESERVES,
  LOST_NOT_PROCEEDING,
  LOST_TO_COMPETION,
  LOST_TO_PRICING,
  NOT_COMPLIANT
];

// Whole-day difference from today to a strict "YYYY-MM-DD" date (both
// locked_exp_date and pipelineData.coe are stored in that format). Negative =
// in the past. Returns null for missing/invalid dates.
function daysUntilYmd(ymd) {
  if (!ymd) return null;
  const target = moment(ymd, "YYYY-MM-DD", true);
  if (!target.isValid()) return null;
  return target.startOf("day").diff(moment().startOf("day"), "days");
}

function isDecided(doc) {
  const pd = doc.pipelineData || {};
  return (
    pd.archived === true || ALERT_SUPPRESS_STATUSES.includes(pd.status)
  );
}

// Locked loan whose lock is expiring/expired. null | {level, days, expired}.
function lockAlert(doc) {
  if (isDecided(doc)) return null;
  const md = doc.metadata || {};
  if (!md.locked_exp_date) return null; // unlocked -> handled by coeAlert
  const days = daysUntilYmd(md.locked_exp_date);
  if (days == null) return null;
  if (days <= LOCK_URGENT_DAYS) {
    return { level: "urgent", days, expired: days < 0 };
  }
  if (days <= LOCK_WARN_DAYS) return { level: "warn", days, expired: false };
  return null;
}

// Unlocked loan approaching its COE. null | {level, days}.
function coeAlert(doc) {
  if (isDecided(doc)) return null;
  const md = doc.metadata || {};
  if (md.locked_exp_date) return null; // locked -> handled by lockAlert
  // COE flows from the LE Price Eng page (metadata.coe); fall back to a legacy
  // pipeline-entered value.
  const days = daysUntilYmd(md.coe || (doc.pipelineData || {}).coe);
  if (days == null || days < 0) return null;
  if (days <= COE_URGENT_DAYS) return { level: "urgent", days };
  if (days <= COE_WARN_DAYS) return { level: "warn", days };
  return null;
}

function DateFieldMask({
  children,
  fieldName,
  value,
  onChange,
  onBlurChange,
  readOnly
}) {
  const [showMask, setShowMask] = useState(true);
  const dateRef = useRef();

  useLayoutEffect(() => {
    if (!showMask) {
      dateRef.current.setFocus();
    }
  }, [showMask]);

  function onMaskClick() {
    if (readOnly) {
      return;
    }
    setShowMask(false);
  }

  function onDateFieldBlur() {
    setShowMask(true);
  }

  return (
    <div>
      {showMask && (
        <div className="pointer" onClick={onMaskClick}>
          {children}
        </div>
      )}
      {!showMask && (
        <DateField
          ref={dateRef}
          onBlur={onDateFieldBlur}
          fieldName={fieldName}
          value={value}
          onChange={onChange}
          onBlurChange={onBlurChange}
        />
      )}
    </div>
  );
}

// ── Pipeline Sort/Find helpers ───────────────────────────────────────────────
export const PIPELINE_SORT_FIELDS = [
  { value: "borrower", label: "Borrower (Last, First)" },
  { value: "additional_details", label: "Additional Details" },
  { value: "approval_date", label: "Approval Date" },
  { value: "clear_to_close", label: "Clear to Close (CTC)" },
  { value: "coe", label: "COE" },
  { value: "docs_out", label: "Docs Out" },
  { value: "fund_date", label: "Fund Date" },
  { value: "loan_number", label: "Loan Number" },
  { value: "locked_exp_date", label: "Locked Exp Date" },
  { value: "registered_submitted", label: "Registered Submitted" },
  { value: "status", label: "Status" },
  { value: "unlocked", label: "Unlocked Loans" },
  { value: "dob", label: "Date of Birth" }
];

const PIPELINE_DATE_FIELDS = [
  "coe",
  "fund_date",
  "locked_exp_date",
  "approval_date",
  "registered_submitted",
  "clear_to_close",
  "docs_out",
  "dob"
];

const DATE_PARSE_FORMATS = ["YYYY-MM-DD", "MM/DD/YYYY", "M/D/YYYY", "M/D/YY"];

function pipelineBorrowers(doc) {
  return (doc.metadata && doc.metadata.borrowers) || [];
}

function pipelineFieldValue(doc, field) {
  const md = doc.metadata || {};
  const pd = doc.pipelineData || {};
  const b0 = pipelineBorrowers(doc)[0] || {};
  switch (field) {
    case "borrower":
      return (
        `${b0.last_name || ""} ${b0.first_name || ""}`.trim() ||
        (md.borrower_names && md.borrower_names[0]) ||
        ""
      );
    case "status":
      return LOAN_STATUS_NAMES[pd.status] || "";
    case "loan_number":
      return md.loan_number || "";
    case "coe":
      return md.coe || pd.coe || "";
    case "fund_date":
      return md.fund_date || pd.funded_disposition || "";
    case "locked_exp_date":
      return md.locked_exp_date || "";
    case "approval_date":
      return pd.approval_date || "";
    case "registered_submitted":
      return pd.loan_registered_submitted || "";
    case "clear_to_close":
      return pd.clear_to_close || "";
    case "docs_out":
      return pd.docs_out || "";
    case "additional_details":
      return pd.additional_details || "";
    case "dob":
      return b0.date_of_birth || "";
    default:
      return "";
  }
}

function pipelineMatchesFind(doc, findField, findText) {
  const q = (findText || "").trim().toLowerCase();
  if (!q) return true;
  const b0 = pipelineBorrowers(doc)[0] || {};
  const b1 = pipelineBorrowers(doc)[1] || {};
  const map = {
    borrower_first: [b0.first_name],
    borrower_last: [b0.last_name],
    co_first: [b1.first_name],
    co_last: [b1.last_name],
    dob: [b0.date_of_birth, b1.date_of_birth],
    all: [
      b0.first_name,
      b0.last_name,
      b1.first_name,
      b1.last_name,
      b0.date_of_birth,
      b1.date_of_birth,
      doc.name
    ]
  };
  return (map[findField] || map.all).some(v =>
    (v || "").toString().toLowerCase().includes(q)
  );
}

function pipelineMatchesStatus(doc, statusFilter) {
  if (!statusFilter || statusFilter === "all") return true;
  return (doc.pipelineData || {}).status === statusFilter;
}

function pipelineMatchesKeyDate(doc, field, range, customFrom, customTo) {
  if (!field) return true;
  const raw = pipelineFieldValue(doc, field);
  if (!raw) return false;
  const d = moment(raw, DATE_PARSE_FORMATS, false);
  if (!d.isValid()) return false;
  const now = moment();
  if (field === "dob") {
    // Birthday windows compare month/day, ignoring year.
    const bday = moment({ year: now.year(), month: d.month(), date: d.date() });
    if (range === "this_week") {
      return bday.isBetween(
        now.clone().startOf("week"),
        now.clone().endOf("week"),
        "day",
        "[]"
      );
    }
    if (range === "next_30") {
      return bday.isBetween(
        now.clone().startOf("day"),
        now.clone().add(30, "days"),
        "day",
        "[]"
      );
    }
    return d.month() === now.month();
  }
  if (range === "this_week") {
    return d.isBetween(
      now.clone().startOf("week"),
      now.clone().endOf("week"),
      "day",
      "[]"
    );
  }
  if (range === "next_30") {
    return d.isBetween(
      now.clone().startOf("day"),
      now.clone().add(30, "days"),
      "day",
      "[]"
    );
  }
  if (range === "custom") {
    const from = customFrom ? moment(customFrom) : null;
    const to = customTo ? moment(customTo) : null;
    if (from && from.isValid() && d.isBefore(from, "day")) return false;
    if (to && to.isValid() && d.isAfter(to, "day")) return false;
    return true;
  }
  return d.isSame(now, "month") && d.isSame(now, "year");
}

function pipelineCompare(a, b, sortField, sortDir) {
  const dir = sortDir === "desc" ? -1 : 1;
  // Unlocked loans (blank Locked Exp Date) sort first, so LOs can catch loans
  // that were never locked.
  if (sortField === "unlocked") {
    const au = pipelineFieldValue(a, "locked_exp_date") ? 1 : 0;
    const bu = pipelineFieldValue(b, "locked_exp_date") ? 1 : 0;
    return (au - bu) * dir;
  }
  const av = pipelineFieldValue(a, sortField);
  const bv = pipelineFieldValue(b, sortField);
  if (PIPELINE_DATE_FIELDS.includes(sortField)) {
    const am = moment(av, DATE_PARSE_FORMATS, false);
    const bm = moment(bv, DATE_PARSE_FORMATS, false);
    const at = am.isValid() ? am.valueOf() : Infinity; // blanks sort last
    const bt = bm.isValid() ? bm.valueOf() : Infinity;
    return (at - bt) * dir;
  }
  if (sortField === "loan_number") {
    const an = av === "" ? Infinity : parseFloat(av);
    const bn = bv === "" ? Infinity : parseFloat(bv);
    return ((isNaN(an) ? Infinity : an) - (isNaN(bn) ? Infinity : bn)) * dir;
  }
  return (
    av.toString().toLowerCase().localeCompare(bv.toString().toLowerCase()) * dir
  );
}

export default function HomePage() {
  const [{ accountProfile, companyProfile }, { logout }] = useUser();
  const [documents, setDocuments] = useState([]);
  // Non-destructive load-failure notice. A failed pipeline refresh (e.g. the
  // reload after "Update", a brief deploy hand-off, or a dropped connection)
  // must NEVER auto-log-out the LO or discard work — that ejected Edge users on
  // Update. Instead we surface this banner and keep the session; the LO can
  // Retry, or choose to log in again.
  const [loadError, setLoadError] = useState(null); // null | "auth" | "network"
  const [documentName, setDocumentName] = useState("");
  const [documentType, setDocumentType] = useState(PURCHASE);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [fnmImporting, setFnmImporting] = useState(false);
  // Import File menu: which format was chosen ("fnm34" | "fnm32"). The loan type
  // (Purchase/Refinance) comes from the menu choice, not the file, so the LO
  // explicitly declares it.
  const [importFormat, setImportFormat] = useState(null);
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const importMenuRef = useRef(null);
  const [importDocument, setImportDocument] = useState(null);
  // Optional debt filters on the import dialog (POINT parity).
  const [ignoreZeroBalance, setIgnoreZeroBalance] = useState(false);
  const [ignoreZeroPayment, setIgnoreZeroPayment] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [errorMessages, setErrorMessages] = useState([]);
  // Remember the last-used pipeline tab (Time Sensitive / Purchase / Refinance)
  // so an update-reload (or any in-session refresh) returns the LO to the tab
  // they were on. STANDARD RULE: the app always OPENS on Time Sensitive — a
  // fresh login or a newly-opened app must start there. We use sessionStorage
  // (not localStorage) so the memory survives a same-tab reload/upgrade but is
  // empty when the browser/app is reopened; login and logout also clear it
  // (see user_service.js), so a fresh login lands on Time Sensitive too.
  const [currentSection, setCurrentSectionState] = useState(() => {
    try {
      // Purge the dead pre-V29.85 localStorage key (that version persisted the
      // tab in localStorage, which wrongly survived logins/app-opens). It is no
      // longer read; remove it so no stale value can ever influence behavior.
      localStorage.removeItem("lanis_pipeline_tab");
    } catch (e) {
      // ignore
    }
    try {
      const saved = sessionStorage.getItem("lanis_pipeline_tab");
      if (saved === PURCHASE || saved === REFINANCE || saved === "TIME_SENSITIVE") {
        return saved;
      }
    } catch (e) {
      // sessionStorage unavailable — fall through to the default
    }
    return "TIME_SENSITIVE";
  });
  function setCurrentSection(section) {
    setCurrentSectionState(section);
    try {
      sessionStorage.setItem("lanis_pipeline_tab", section);
    } catch (e) {
      // ignore; persistence is a convenience, not required
    }
  }
  const [showClosed, setShowClosed] = useState(NO);
  const [showArchived, setShowArchived] = useState(NO);
  // The loan (document) pending the archive-confirmation dialog, or null.
  const [archiveDialogDoc, setArchiveDialogDoc] = useState(null);
  // Copy Loan: duplicate an entire loan file under a new name.
  const [copyDialogDoc, setCopyDialogDoc] = useState(null);
  const [copyName, setCopyName] = useState("");
  const [copyKeepDates, setCopyKeepDates] = useState(NO);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyError, setCopyError] = useState("");
  // Pipeline Sort/Find tool state.
  const [findField, setFindField] = useState("all");
  const [findText, setFindText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Rate-lock / COE risk summary filter: null | "lock" | "coe".
  const [riskFilter, setRiskFilter] = useState(null);
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [keyDateField, setKeyDateField] = useState("");
  const [keyDateRange, setKeyDateRange] = useState("this_month");
  const [keyDateFrom, setKeyDateFrom] = useState("");
  const [keyDateTo, setKeyDateTo] = useState("");
  const fileInputEl = useRef();
  const filteredDocuments = useMemo(
    () =>
      documents.filter(doc => {
        const pd = doc.pipelineData;
        // Archived loans are hidden unless "Show Archived" is on.
        if (pd && pd.archived) {
          return showArchived === YES;
        }
        return !pd || pd.status !== CLOSED || showClosed === YES;
      }),
    [documents, showClosed, showArchived]
  );
  // At-risk loans across the active pipeline (drives the summary bar + filter).
  const lockRiskDocs = useMemo(
    () => filteredDocuments.filter(doc => lockAlert(doc)),
    [filteredDocuments]
  );
  const coeRiskDocs = useMemo(
    () => filteredDocuments.filter(doc => coeAlert(doc)),
    [filteredDocuments]
  );
  // Apply Find + Status + Key Date + Risk filters, then sort. Default order (no
  // sort field) keeps the last-modified-first order that getDocuments established.
  const matchedDocuments = useMemo(() => {
    const result = filteredDocuments.filter(
      doc =>
        pipelineMatchesFind(doc, findField, findText) &&
        pipelineMatchesStatus(doc, statusFilter) &&
        pipelineMatchesKeyDate(
          doc,
          keyDateField,
          keyDateRange,
          keyDateFrom,
          keyDateTo
        ) &&
        (riskFilter === "lock"
          ? !!lockAlert(doc)
          : riskFilter === "coe"
          ? !!coeAlert(doc)
          : true)
    );
    if (sortField) {
      return [...result].sort((a, b) =>
        pipelineCompare(a, b, sortField, sortDir)
      );
    }
    return result;
  }, [
    filteredDocuments,
    findField,
    findText,
    statusFilter,
    keyDateField,
    keyDateRange,
    keyDateFrom,
    keyDateTo,
    riskFilter,
    sortField,
    sortDir
  ]);
  const timeSensitivePipeline = useMemo(
    () =>
      matchedDocuments.filter(
        doc => doc.metadata && doc.metadata.time_sensitive === YES
      ),
    [matchedDocuments]
  );
  const purchasePipeline = useMemo(
    () =>
      matchedDocuments.filter(
        doc =>
          doc.type === PURCHASE &&
          (!doc.metadata || doc.metadata.time_sensitive !== YES)
      ),
    [matchedDocuments]
  );
  const refinancePipeline = useMemo(
    () =>
      matchedDocuments.filter(
        doc =>
          doc.type === REFINANCE &&
          (!doc.metadata || doc.metadata.time_sensitive !== YES)
      ),
    [matchedDocuments]
  );
  // The loans visible in the tab the LO is currently looking at. The pipeline
  // ALWAYS splits loans across the three tabs (Time Sensitive / Purchase /
  // Refinance), so a loan missing from one tab is on another — never lost. The
  // file count and tab badges below read from these so the UI shows exactly
  // where every loan is, instead of a global count that made a sparse tab look
  // like loans had vanished.
  const currentList =
    currentSection === "TIME_SENSITIVE"
      ? timeSensitivePipeline
      : currentSection === PURCHASE
      ? purchasePipeline
      : refinancePipeline;

  // When a Find / Status / Key Date filter is active and the tab in view has no
  // matches but another tab does, jump to a tab that has results — so a search
  // for a loan that lives on a different tab never looks like "nothing came back".
  useEffect(() => {
    const anyFilter =
      findText.trim() !== "" || statusFilter !== "all" || !!keyDateField;
    if (!anyFilter || currentList.length > 0) {
      return;
    }
    if (timeSensitivePipeline.length > 0) {
      setCurrentSection("TIME_SENSITIVE");
    } else if (purchasePipeline.length > 0) {
      setCurrentSection(PURCHASE);
    } else if (refinancePipeline.length > 0) {
      setCurrentSection(REFINANCE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    findText,
    statusFilter,
    keyDateField,
    currentSection,
    timeSensitivePipeline,
    purchasePipeline,
    refinancePipeline
  ]);

  useEffect(() => {
    if (!importFile) {
      return;
    }

    let ignore = false;

    const reader = new FileReader();
    reader.onload = () => {
      if (!ignore) {
        try {
          // The code gen sometimes compiles this poorly, seemingly.
          // Picking non-conflicting names should prevent the issue.
          // Loan type is already set from the Import File menu choice; the
          // file only supplies the data. 3.4 = MISMO XML, 3.2 = Fannie DU text.
          const { document: _document } =
            importFormat === "fnm34"
              ? fromMismoXml(reader.result, { documentType })
              : fromFnmText(reader.result, {});
          setImportDocument(_document);
          setErrorMessages([]);
        } catch (e) {
          console.error("Error converting FNM to object:", e);
          setErrorMessages([`Error reading FNM: ${e.message || "Unknown"}`]);
        }
      }
    };
    reader.readAsText(importFile);

    return () => {
      ignore = true;
    };
  }, [importFile, importFormat, documentType]);

  useEffect(() => {
    function handleImportClickOutside(e) {
      if (importMenuRef.current && !importMenuRef.current.contains(e.target)) {
        setImportMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleImportClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleImportClickOutside);
  }, []);

  function onImportFileChange(e) {
    const file = e.target.files[0];

    if (!file) {
      setImportFile(null);
      setErrorMessages(["No file selected."]);
      return;
    }
    setImportFile(file);
    setErrorMessages([]);
  }

  const greenStatuses = [
    APPROVED_DU,
    APPROVED_UW,
    CLEARED_TO_CLOSE,
    DPA_CLEARED,
    DPA_SUBMITTED,
    NEW,
    PTD_COND,
    PTF_COND,
    SUBMITTED_TO_PROCESSOR,
    WORKING_NOT_SUBMITTED
  ];
  const inverseGreenStatuses = [CLOSED, CD_DISCLOSED, DOCS_OUT, FUNDED];
  const redStatuses = [
    DENIED,
    DENIED_CREDIT,
    DENIED_COMPLIANCE,
    DENIED_COST_TO_CURE,
    DENIED_QUAL_RATE,
    DENIED_RESERVES,
    LOST_NOT_PROCEEDING,
    LOST_TO_COMPETION,
    LOST_TO_PRICING
  ];
  const redOnWhiteStatuses = [
    DISCLOSE_LE,
    DISCLOSED_LE_NOT_SUBMITTED,
    NOT_COMPLIANT
  ];
  const yellowStatuses = [NOTES, ON_HOLD, TBD_REPORT];

  const getDocuments = async (isBackground = false) => {
    // Clicking "Update" reloads the tab; the pipeline then loads in the foreground.
    // During that brief window (and any deploy hand-off) the very first request can
    // blip — including a transient 401 before the token is accepted. Retry a few
    // times BEFORE deciding it's a real auth failure, so an Update never false-ejects
    // a valid session to the Login page (this matches loadDocument's retry). A truly
    // expired/invalid token still 401s on every attempt and correctly logs out.
    // Ride out the reload/deploy window generously before giving up. The first
    // request after an "Update" reload can blip for several seconds.
    const maxAttempts = isBackground ? 1 : 6;
    let lastError = null;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const loanDocuments = await DocumentService.getPipelineData();
        loanDocuments
          .sort((docA, docB) => docB.modified - docA.modified)
          .forEach(loanDocument => {
            loanDocument.metadata =
              loanDocument.metadata && JSON.parse(loanDocument.metadata);
            loanDocument.pipelineData =
              loanDocument.pipelineData && JSON.parse(loanDocument.pipelineData);
          });
        if (!isBackground) setLoadError(null);
        return loanDocuments;
      } catch (e) {
        lastError = e;
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        }
      }
    }
    // A background auto-refresh must never disturb a working session. For a
    // foreground load that still failed after retries, DO NOT auto-logout:
    // that nuked the token and ejected the LO (Edge, where HTTP/2 makes a 401's
    // statusText "Unauthorized" so the old gate fired; Chrome left it empty and
    // silently ignored it). Instead surface a non-destructive banner and keep
    // the session, so no unsaved work is ever lost. Use the reliable numeric
    // status, not statusText.
    if (!isBackground) {
      const status = lastError && (lastError.status || 0);
      const unauthorized =
        status === 401 ||
        (lastError && lastError.statusText === "Unauthorized");
      setLoadError(unauthorized ? "auth" : "network");
    }
  };

  // Retry the foreground pipeline load from the error banner.
  const retryLoadDocuments = () => {
    setLoadError(null);
    getDocuments().then(newDocuments => {
      if (newDocuments) setDocuments(withPendingEdits(newDocuments));
    });
  };

  const showCreateNameModal = type => {
    setDocumentType(type);
    setShowCreateModal(true);
  };

  // Import File menu: LO picks the format + loan type; then the modal opens for
  // the file. Type is declared here (not read from the file).
  const startImport = (format, type) => {
    setImportMenuOpen(false);
    setImportFormat(format);
    setDocumentType(type);
    setFnmImporting(true);
    setShowCreateModal(true);
  };

  const hideCreateNameModal = () => {
    setDocumentName("");
    setFnmImporting(false);
    setImportFormat(null);
    setErrorMessages([]);
    setImportFile(null);
    setImportDocument(null);
    setIgnoreZeroBalance(false);
    setIgnoreZeroPayment(false);
    setShowCreateModal(false);
  };

  const createDocument = async (documentToCreate = fake_state_todo_remove) => {
    try {
      const response = await DocumentService.createDocument(
        documentType,
        documentToCreate,
        default_metadata,
        documentName
      );
      hideCreateNameModal();
      window.open(`/loan-tool/${response.id}`, "_blank");
    } catch (e) {
      // TODO: do something.
    }
  };

  const saveStatus = async (status, id) => {
    try {
      return await DocumentService.savePipelineData(status, id);
    } catch (e) {
      // TODO: do something.
    }
  };

  const openCopyDialog = doc => {
    setCopyDialogDoc(doc);
    setCopyName(`${doc.name} Copy`);
    setCopyKeepDates(NO);
    setCopyError("");
  };

  const closeCopyDialog = () => {
    setCopyDialogDoc(null);
    setCopyName("");
    setCopyKeepDates(NO);
    setCopyBusy(false);
    setCopyError("");
  };

  /**
   * Duplicate an entire loan file. The whole loan lives in one JSON document,
   * so the copy carries everything automatically — applicant, all loans,
   * closing costs, REO, debts, income, reserves.
   *
   * "Keep dates and lock information" controls the COE / lock / approval /
   * funding dates. Status, Loan Number and Archived ALWAYS reset: a copy has
   * not been submitted or approved, and reusing the lender's loan number on a
   * second file causes confusion.
   */
  const copyDocument = async () => {
    if (!copyDialogDoc || !copyName) return;
    setCopyBusy(true);
    setCopyError("");
    try {
      const response = await DocumentService.getDocument(copyDialogDoc.id);
      const source = response.document;
      const content = JSON.parse(source.content);
      const sourceMeta = source.metadata
        ? JSON.parse(source.metadata)
        : { ...default_metadata };

      const keep = copyKeepDates === YES;
      const newMeta = {
        ...sourceMeta,
        loan_number: "",
        locked_exp_date: keep ? sourceMeta.locked_exp_date : "",
        fund_date: keep ? sourceMeta.fund_date : "",
        time_sensitive: keep ? sourceMeta.time_sensitive : ""
      };

      const created = await DocumentService.createDocument(
        source.type,
        content,
        newMeta,
        copyName
      );

      if (keep) {
        const sourcePipeline = copyDialogDoc.pipelineData || {};
        await saveStatus(
          {
            ...default_pipeline_data,
            coe: sourcePipeline.coe || "",
            loan_registered_submitted:
              sourcePipeline.loan_registered_submitted || "",
            approval_date: sourcePipeline.approval_date || "",
            clear_to_close: sourcePipeline.clear_to_close || "",
            docs_out: sourcePipeline.docs_out || "",
            funded_disposition: sourcePipeline.funded_disposition || "",
            additional_details: sourcePipeline.additional_details || ""
          },
          created.id
        );
      }

      closeCopyDialog();
      window.open(`/loan-tool/${created.id}`, "_blank");
    } catch (e) {
      console.error("Error copying loan file:", e);
      setCopyError("Couldn't copy this loan file. Please try again.");
      setCopyBusy(false);
    }
  };

  // Pipeline edits that haven't reached the server yet: document id -> the
  // loan's full pipelineData with the edits. Dates used to save only when the
  // field reported losing focus, and the date picker never reports that while
  // its calendar is open (it opens as soon as you click in), so a typed date
  // and then a click elsewhere, or on Update now, was never saved. Now every
  // edit auto-saves shortly after it's made, the Save button saves right away,
  // and Update now / leaving the page wait for (or warn about) anything pending.
  const pendingPipelineRef = useRef({});
  const autoSaveTimerRef = useRef(null);
  // Saves run one after another, so an older save can never land after a newer one.
  const saveChainRef = useRef(Promise.resolve(true));
  // "saved" | "pending" | "saving" | "error" | "auth" (login expired)
  const [pipelineSaveState, setPipelineSaveState] = useState("saved");

  const savePendingPipeline = () => {
    clearTimeout(autoSaveTimerRef.current);
    saveChainRef.current = saveChainRef.current.then(async () => {
      const entries = Object.entries(pendingPipelineRef.current);
      if (entries.length === 0) return true;
      setPipelineSaveState("saving");
      let failure = null;
      for (const [id, data] of entries) {
        try {
          await DocumentService.savePipelineData(data, id);
          // Keep it pending if a newer edit arrived while this one was saving.
          if (pendingPipelineRef.current[id] === data) {
            delete pendingPipelineRef.current[id];
          }
        } catch (e) {
          failure = e && e.status === 401 ? "auth" : "error";
        }
      }
      setPipelineSaveState(
        failure ||
          (Object.keys(pendingPipelineRef.current).length ? "pending" : "saved")
      );
      return !failure;
    });
    return saveChainRef.current;
  };

  // Show unsaved edits over a fresh server copy, so a background refresh
  // never puts an old value back on screen before its save lands.
  const withPendingEdits = loanDocuments =>
    loanDocuments.map(loanDocument =>
      pendingPipelineRef.current[loanDocument.id]
        ? {
            ...loanDocument,
            pipelineData: pendingPipelineRef.current[loanDocument.id]
          }
        : loanDocument
    );

  useEffect(() => registerPendingSaves(savePendingPipeline), []);

  // Closing or reloading the tab with Pipeline edits still unsaved -> the
  // browser's "Leave site?" warning, same as a loan with unsaved changes.
  useEffect(() => {
    function warnIfUnsaved(e) {
      if (Object.keys(pendingPipelineRef.current).length > 0) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    }
    window.addEventListener("beforeunload", warnIfUnsaved);
    return () => window.removeEventListener("beforeunload", warnIfUnsaved);
  }, []);

  const onPipelineDataChange = async (updatedPipelineData, id, isSave) => {
    const document = documents.find(document => document.id === id);
    if (!document) return;

    const newPipelineData = {
      ...(pendingPipelineRef.current[id] ||
        document.pipelineData ||
        default_pipeline_data),
      ...updatedPipelineData
    };
    pendingPipelineRef.current[id] = newPipelineData;
    setDocuments(
      documents.map(d => (d.id === id ? { ...d, pipelineData: newPipelineData } : d))
    );

    if (isSave) {
      await savePendingPipeline();
    } else {
      setPipelineSaveState("pending");
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(savePendingPipeline, 1500);
    }
  };

  // Expired login on a Pipeline save: log in on a NEW tab (logging in here
  // would reload this page and drop the unsaved edits). The old token is dead
  // on the server, so clearing it opens the new tab on the Login page; its new
  // token is shared with this tab, so Save then works here.
  const logInInNewTab = () => {
    removeAccessToken();
    window.open("/", "_blank");
  };

  useEffect(() => {
    getDocuments().then(newDocuments => {
      if (newDocuments) {
        setDocuments(newDocuments);
      }
    });
  }, []);

  // Keep the pipeline current without the LO ever having to reload the browser.
  // Re-pull the loan list when the tab regains focus / becomes visible (e.g. the
  // LO edited a loan in another tab, or came back after hours), and on a slow
  // backstop timer for a tab that stays open and focused all day. A short
  // throttle avoids a refetch storm from rapid focus/visibility events.
  useEffect(() => {
    let active = true;
    let lastFetch = Date.now();
    const MIN_GAP_MS = 15 * 1000;
    const POLL_MS = 5 * 60 * 1000;
    function refresh(force) {
      if (window.document.visibilityState === "hidden") return;
      if (!force && Date.now() - lastFetch < MIN_GAP_MS) return;
      lastFetch = Date.now();
      getDocuments(true).then(newDocuments => {
        if (active && newDocuments) {
          setDocuments(withPendingEdits(newDocuments));
          setLoadError(null); // a recovered refresh clears any stale error banner
        }
      });
    }
    const onFocus = () => refresh(false);
    const onVisible = () => refresh(false);
    window.addEventListener("focus", onFocus);
    window.document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(() => refresh(true), POLL_MS);
    return () => {
      active = false;
      window.removeEventListener("focus", onFocus);
      window.document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleSort(field) {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function clearFilters() {
    setFindField("all");
    setFindText("");
    setStatusFilter("all");
    setRiskFilter(null);
    setSortField("");
    setSortDir("asc");
    setKeyDateField("");
    setKeyDateRange("this_month");
    setKeyDateFrom("");
    setKeyDateTo("");
  }

  function renderFilter() {
    const statusOptions = Object.entries(LOAN_STATUS_NAMES).sort((a, b) =>
      a[1].localeCompare(b[1])
    );
    const quickSortCols = [
      { value: "borrower", label: "Borrower" },
      { value: "status", label: "Status" },
      { value: "coe", label: "COE" },
      { value: "fund_date", label: "Fund Date" },
      { value: "loan_number", label: "Loan #" }
    ];
    // Count the filters that can narrow the list (hide results), so we can flag
    // when something is filtering — Sort/Show Closed don't hide, so they're excluded.
    const activeFilterCount =
      (findText.trim() ? 1 : 0) +
      (statusFilter !== "all" ? 1 : 0) +
      (keyDateField ? 1 : 0);
    return (
      <div className="pipeline-sort-find">
        <div className="psf-row">
          <span className="psf-label">Find</span>
          <select
            className="psf-select"
            value={findField}
            onChange={e => setFindField(e.target.value)}
          >
            <option value="all">All name fields</option>
            <option value="borrower_first">Borrower First Name</option>
            <option value="borrower_last">Borrower Last Name</option>
            <option value="co_first">Co-Borrower First Name</option>
            <option value="co_last">Co-Borrower Last Name</option>
            <option value="dob">Date of Birth</option>
          </select>
          <input
            className={class_list(
              "psf-input",
              findText.trim() && "psf-active"
            )}
            placeholder="Type a name or date…"
            value={findText}
            onChange={e => setFindText(e.target.value)}
          />
          <span className="psf-count">
            {activeFilterCount > 0 && (
              <span className="psf-active-badge">
                {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""} on
              </span>
            )}
            {currentList.length} of {matchedDocuments.length} files
            {matchedDocuments.length > currentList.length && (
              <span className="psf-othertabs">
                {" "}
                &middot; {matchedDocuments.length - currentList.length} on other
                tab{matchedDocuments.length - currentList.length > 1 ? "s" : ""}
              </span>
            )}
          </span>
        </div>

        <div className="psf-row">
          <span className="psf-label">Sort by</span>
          <select
            className="psf-select"
            value={sortField}
            onChange={e => setSortField(e.target.value)}
          >
            <option value="">Default (last worked)</option>
            {PIPELINE_SORT_FIELDS.map(f => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <button
            className="psf-dir"
            title="Ascending / Descending"
            onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
          >
            {sortDir === "asc" ? "↑" : "↓"}
          </button>

          <span className="psf-label">Status</span>
          <select
            className={class_list(
              "psf-select",
              statusFilter !== "all" && "psf-active"
            )}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            {statusOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>

          <span className="psf-label">Key Date</span>
          <select
            className={class_list(
              "psf-select",
              keyDateField && "psf-active"
            )}
            value={keyDateField}
            onChange={e => setKeyDateField(e.target.value)}
          >
            <option value="">Select date…</option>
            <option value="coe">COE</option>
            <option value="fund_date">Fund Date</option>
            <option value="locked_exp_date">Locked Exp Date</option>
            <option value="approval_date">Approval Date</option>
            <option value="dob">Date of Birth</option>
          </select>
          {keyDateField && (
            <select
              className="psf-select"
              value={keyDateRange}
              onChange={e => setKeyDateRange(e.target.value)}
            >
              <option value="this_week">This week</option>
              <option value="this_month">This month</option>
              <option value="next_30">Next 30 days</option>
              <option value="custom">Custom range…</option>
            </select>
          )}
          {keyDateField && keyDateRange === "custom" && (
            <>
              <input
                type="date"
                className="psf-date"
                value={keyDateFrom}
                onChange={e => setKeyDateFrom(e.target.value)}
              />
              <input
                type="date"
                className="psf-date"
                value={keyDateTo}
                onChange={e => setKeyDateTo(e.target.value)}
              />
            </>
          )}

          <SelectField
            fieldName="value"
            name="Show Closed Documents"
            value={showClosed}
            onChange={updates => setShowClosed(updates.value)}
            selectOptions={YES_NO}
          />

          <SelectField
            fieldName="value"
            name="Show Archived"
            value={showArchived}
            onChange={updates => setShowArchived(updates.value)}
            selectOptions={YES_NO}
          />

          <button
            className={class_list(
              "psf-clear",
              activeFilterCount > 0 && "psf-clear-active"
            )}
            onClick={clearFilters}
          >
            Clear{activeFilterCount > 0 ? " filters" : ""}
          </button>
        </div>

        <div className="psf-row psf-quicksort">
          <span className="psf-label">Quick sort</span>
          {quickSortCols.map(c => (
            <button
              key={c.value}
              className={class_list(
                "psf-chip",
                sortField === c.value && "active"
              )}
              onClick={() => toggleSort(c.value)}
            >
              {c.label}
              {sortField === c.value ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
            </button>
          ))}
        </div>
      </div>
    );
  }

  function renderList(docs) {
    return docs.map(document => {
      const metadata = document.metadata || {};
      const pipelineData = document.pipelineData || {};
      const lockRisk = lockAlert(document);
      const coeRisk = coeAlert(document);
      // Fund Date comes from the LE Price Eng page (applicant.fund_date). Read it
      // from metadata first (rebuilt on save), then fall back to the loan content
      // and legacy pipeline field so it shows regardless of how the doc was saved.
      let contentFundDate = "";
      let contentCoe = "";
      try {
        const parsedContent =
          document.content &&
          (typeof document.content === "string"
            ? JSON.parse(document.content)
            : document.content);
        contentFundDate =
          (parsedContent &&
            parsedContent.applicant &&
            parsedContent.applicant.fund_date) ||
          "";
        contentCoe =
          (parsedContent &&
            parsedContent.applicant &&
            parsedContent.applicant.estimated_close_of_escrow) ||
          "";
      } catch (e) {
        contentFundDate = "";
        contentCoe = "";
      }
      const fundDate =
        metadata.fund_date || contentFundDate || pipelineData.funded_disposition || "";
      // COE flows from the LE Price Eng "Estimated Close Of Escrow (COE)"
      // (applicant.estimated_close_of_escrow -> metadata.coe), same as Fund Date.
      const coeDate = metadata.coe || contentCoe || pipelineData.coe || "";
      return (
        <div key={document.id} className="pipeline-entry-container">
          <div className="pipeline-entry-title">
            {companyProfile.subscription_active ? (
              <a
                className="button"
                href={`/loan-tool/${document.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {document.name}
              </a>
            ) : (
              <button className="button" disabled={true}>
                {document.name}
              </button>
            )}
            {metadata.time_sensitive === YES && (
              <span className="time-sensitive-badge">Time Sensitive</span>
            )}
            {companyProfile.subscription_active && (
              <span className="pipeline-archive-actions">
                {pipelineData.archived ? (
                  <>
                    <span className="pipeline-archived-badge">Archived</span>
                    <button
                      className="button inverse-button"
                      onClick={() =>
                        onPipelineDataChange(
                          { archived: false },
                          document.id,
                          true
                        )
                      }
                    >
                      Undo Archive
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="button"
                      onClick={() => openCopyDialog(document)}
                    >
                      Copy Loan
                    </button>
                    <button
                      className="button"
                      onClick={() => setArchiveDialogDoc(document)}
                    >
                      Archive
                    </button>
                  </>
                )}
              </span>
            )}
          </div>
          <div className="pipeline-entry">
            <div className="pipeline-entry-row">
              <div className="pipeline-entry-cell">
                <div className="entry-title">Last Modified</div>
                <div className="bold">
                  {moment(document.modified)
                    .tz(moment.tz.guess())
                    .format("MM/DD/YYYY hh:mm A")}
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div>
                  {metadata.borrower_names && metadata.borrower_names[0]}
                </div>
                <div>
                  {metadata.borrower_names && metadata.borrower_names[2]}
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div>
                  {metadata.borrower_names && metadata.borrower_names[1]}
                </div>
                <div>
                  {metadata.borrower_names && metadata.borrower_names[3]}
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">1st Loan Amount</div>
                <div>
                  <CurrencyField
                    naked
                    fixedDigits={0}
                    value={metadata.first_loan_amount}
                  />
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">2nd Loan Amount</div>
                <div>
                  <CurrencyField
                    naked
                    fixedDigits={0}
                    value={metadata.second_loan_amount}
                  />
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Lender</div>
                <div>{metadata.lender}</div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Loan Type</div>
                <div>{LOAN_TYPE_CATEGORY_NAMES[metadata.loan_type]}</div>
              </div>
            </div>
            <div className="pipeline-entry-row">
              <div className="pipeline-entry-cell">
                <div className="entry-title">Registered Submitted</div>
                <div>
                  <DateFieldMask
                    fieldName="loan_registered_submitted"
                    value={pipelineData.loan_registered_submitted}
                    onChange={updates =>
                      onPipelineDataChange(updates, document.id, false)
                    }
                    onBlurChange={updates =>
                      onPipelineDataChange(updates, document.id, true)
                    }
                    readOnly={!companyProfile.subscription_active}
                  >
                    <div
                      className={class_list(
                        "right-angle-box",
                        !pipelineData.loan_registered_submitted &&
                          "unfilled-right-angle-box"
                      )}
                    >
                      <div className="box-content">
                        <DateField
                          naked
                          value={pipelineData.loan_registered_submitted}
                        />
                      </div>
                    </div>
                  </DateFieldMask>
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Locked Exp Date</div>
                <div
                  className={class_list(
                    "right-angle-box",
                    "inverse-right-angle-box",
                    !metadata.locked_exp_date && "unfilled-right-angle-box"
                  )}
                >
                  <div className="box-content">
                    <DateField naked value={metadata.locked_exp_date} />
                  </div>
                </div>
                {lockRisk && (
                  <span
                    className={class_list(
                      "risk-pill",
                      lockRisk.level === "urgent" ? "risk-red" : "risk-amber"
                    )}
                  >
                    {lockRisk.expired
                      ? "Lock expired"
                      : "Lock expires in " + lockRisk.days + "d"}
                  </span>
                )}
                {coeRisk && (
                  <span
                    className={class_list(
                      "risk-pill",
                      coeRisk.level === "urgent" ? "risk-orange" : "risk-yellow"
                    )}
                  >
                    {"Not locked · COE in " + coeRisk.days + "d"}
                  </span>
                )}
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Approval Date</div>
                <div>
                  <DateFieldMask
                    fieldName="approval_date"
                    value={pipelineData.approval_date}
                    onChange={updates =>
                      onPipelineDataChange(updates, document.id, false)
                    }
                    onBlurChange={updates =>
                      onPipelineDataChange(updates, document.id, true)
                    }
                    readOnly={!companyProfile.subscription_active}
                  >
                    <div
                      className={class_list(
                        "right-angle-box",
                        !pipelineData.approval_date &&
                          "unfilled-right-angle-box"
                      )}
                    >
                      <div className="box-content">
                        <DateField naked value={pipelineData.approval_date} />
                      </div>
                    </div>
                  </DateFieldMask>
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Clear to Close (CTC)</div>
                <div>
                  <DateFieldMask
                    fieldName="clear_to_close"
                    value={pipelineData.clear_to_close}
                    onChange={updates =>
                      onPipelineDataChange(updates, document.id, false)
                    }
                    onBlurChange={updates =>
                      onPipelineDataChange(updates, document.id, true)
                    }
                    readOnly={!companyProfile.subscription_active}
                  >
                    <div
                      className={class_list(
                        "right-angle-box",
                        !pipelineData.clear_to_close &&
                          "unfilled-right-angle-box"
                      )}
                    >
                      <div className="box-content">
                        <DateField naked value={pipelineData.clear_to_close} />
                      </div>
                    </div>
                  </DateFieldMask>
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Docs Out</div>
                <div>
                  <DateFieldMask
                    fieldName="docs_out"
                    value={pipelineData.docs_out}
                    onChange={updates =>
                      onPipelineDataChange(updates, document.id, false)
                    }
                    onBlurChange={updates =>
                      onPipelineDataChange(updates, document.id, true)
                    }
                    readOnly={!companyProfile.subscription_active}
                  >
                    <div
                      className={class_list(
                        "right-angle-box",
                        !pipelineData.docs_out && "unfilled-right-angle-box"
                      )}
                    >
                      <div className="box-content">
                        <DateField naked value={pipelineData.docs_out} />
                      </div>
                    </div>
                  </DateFieldMask>
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Fund Date</div>
                <div>
                  {/* Auto-filled from the LE Price Eng "Fund Date"
                      (applicant.fund_date -> metadata.fund_date); TBD if empty. */}
                  <div
                    className={class_list(
                      "right-angle-box",
                      !fundDate && "unfilled-right-angle-box"
                    )}
                  >
                    <div className="box-content">
                      {fundDate ? <DateField naked value={fundDate} /> : "TBD"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="pipeline-entry-row">
              <div className="pipeline-entry-cell">
                <div className="entry-title">Loan Number</div>
                <div>{metadata.loan_number}</div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">COE</div>
                <div>
                  {/* Auto-filled from the LE Price Eng "Estimated Close Of Escrow
                      (COE)" (applicant.estimated_close_of_escrow -> metadata.coe);
                      TBD if empty. */}
                  <div
                    className={class_list(
                      "right-angle-box",
                      "inverse-right-angle-box",
                      !coeDate && "unfilled-right-angle-box"
                    )}
                  >
                    <div className="box-content">
                      {coeDate ? <DateField naked value={coeDate} /> : "TBD"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Refinance Purchase</div>
                <div>{DOCUMENT_TYPES[document.type]}</div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Additional Details</div>
                <div>
                  <TextField
                    fieldName="additional_details"
                    value={pipelineData.additional_details}
                    onChange={updates =>
                      onPipelineDataChange(updates, document.id, false)
                    }
                    onBlurChange={updates =>
                      onPipelineDataChange(updates, document.id, true)
                    }
                    readOnly={!companyProfile.subscription_active}
                  />
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Status</div>
                <div className="loan-status-container">
                  <SelectField
                    className={class_list(
                      greenStatuses.includes(pipelineData.status) &&
                        "green-status",
                      inverseGreenStatuses.includes(pipelineData.status) &&
                        "inverse-green-status",
                      redStatuses.includes(pipelineData.status) && "red-status",
                      redOnWhiteStatuses.includes(pipelineData.status) &&
                        "red-on-white-status",
                      yellowStatuses.includes(pipelineData.status) &&
                        "yellow-status",
                      // Disclose LE and CD Disclosed both start a 3-day
                      // borrower-facing clock (deliver the LE / sign the CD): flag
                      // the Status with a red border until the LO advances to any
                      // other status.
                      (pipelineData.status === DISCLOSE_LE ||
                        pipelineData.status === CD_DISCLOSED) &&
                        "disclose-le-flag"
                    )}
                    optionClassNameFunction={value => {
                      if (greenStatuses.includes(value)) {
                        return "green-status";
                      }
                      if (inverseGreenStatuses.includes(value)) {
                        return "inverse-green-status";
                      }
                      if (redStatuses.includes(value)) {
                        return "red-status";
                      }
                      if (redOnWhiteStatuses.includes(value)) {
                        return "red-on-white-status";
                      }
                      if (yellowStatuses.includes(value)) {
                        return "yellow-status";
                      }
                    }}
                    onChange={updates =>
                      onPipelineDataChange(updates, document.id, true)
                    }
                    fieldName="status"
                    value={pipelineData.status || ""}
                    selectOptions={LOAN_STATUS_NAMES}
                    optionOrder={LOAN_STATUS_ORDER}
                    showDefaultOption
                    readOnly={!companyProfile.subscription_active}
                  />
                </div>
              </div>
              <div className="pipeline-entry-cell">
                <div className="entry-title">Percent to Close</div>
                <div>
                  {LOAN_STATUS_PERCENTS[pipelineData.status] && (
                    <PercentField
                      naked
                      fixedDigits={0}
                      value={LOAN_STATUS_PERCENTS[pipelineData.status]}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  return (
    <StandardTemplate basic showMoreLinks>
      <div className="pipeline-page">
        {loadError && (
          <div className="pipeline-load-error no-print">
            <span>
              {loadError === "auth"
                ? "We couldn't refresh your pipeline — your session may have expired. You have not been logged out and no work has been lost."
                : "We couldn't refresh your pipeline. Check your connection and try again."}
            </span>
            <span className="pipeline-load-error-actions">
              <button className="button" onClick={retryLoadDocuments}>
                Retry
              </button>
              <button
                className="button inverse-button"
                onClick={() => logout()}
              >
                Log in again
              </button>
            </span>
          </div>
        )}
        <div className="top-header-section">
          <div className="top-header-section-title">PIPELINE</div>
          {isDashboardEnabled(accountProfile, companyProfile) && (
            <Link to="/dashboard" className="button">
              Dashboard
            </Link>
          )}
          {accountProfile.loan_officer_role && (
            <>
              <button
                className="button"
                onClick={() => showCreateNameModal(PURCHASE)}
                disabled={!companyProfile.subscription_active}
              >
                New Purchase
              </button>
              <button
                className="button"
                onClick={() => showCreateNameModal(REFINANCE)}
                disabled={!companyProfile.subscription_active}
              >
                New Refinance
              </button>
              <div className="import-file-menu" ref={importMenuRef}>
                <button
                  className="button"
                  onClick={() => setImportMenuOpen(!importMenuOpen)}
                  disabled={!companyProfile.subscription_active}
                >
                  Import File
                </button>
                {importMenuOpen && (
                  <ul className="import-file-dropdown">
                    <li>
                      <button
                        className="import-file-item"
                        onClick={() => startImport("fnm34", PURCHASE)}
                      >
                        Fannie Mae 3.4 &ndash; Purchase
                      </button>
                    </li>
                    <li>
                      <button
                        className="import-file-item"
                        onClick={() => startImport("fnm34", REFINANCE)}
                      >
                        Fannie Mae 3.4 &ndash; Refinance
                      </button>
                    </li>
                    <li>
                      <button
                        className="import-file-item"
                        onClick={() => startImport("fnm32", PURCHASE)}
                      >
                        Fannie Mae 3.2 &ndash; Purchase
                      </button>
                    </li>
                    <li>
                      <button
                        className="import-file-item"
                        onClick={() => startImport("fnm32", REFINANCE)}
                      >
                        Fannie Mae 3.2 &ndash; Refinance
                      </button>
                    </li>
                  </ul>
                )}
              </div>
              <TrainingTip content="LANIS Training Tip: Please turn off Pop Up Blockers to see your New File." />
              <a href="https://golanis.com/training-tips/"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="fnm-help-link">FNM Help</a>
            </>
          )}
          {/* Pipeline edits auto-save; this saves any that are still pending
              right now, and shows where things stand. */}
          <span className="pipeline-save">
            <button
              className="button"
              onClick={savePendingPipeline}
              disabled={pipelineSaveState === "saving"}
            >
              {pipelineSaveState === "saving" ? "Saving..." : "Save"}
            </button>
            <span
              className={class_list(
                "pipeline-save-status",
                pipelineSaveState === "pending" && "pipeline-save-pending"
              )}
            >
              {pipelineSaveState === "pending"
                ? "Unsaved changes"
                : pipelineSaveState === "saved"
                ? "All changes saved"
                : ""}
            </span>
          </span>
        </div>
        {(pipelineSaveState === "error" || pipelineSaveState === "auth") && (
          <div className="pipeline-save-error no-print">
            <span>
              {pipelineSaveState === "auth"
                ? "NOT SAVED: your login session expired, so your Pipeline changes did not reach the server. They are still on this screen. Do NOT reload or close this tab. Log in on a new tab, then come back here and click Save."
                : "NOT SAVED: we couldn't reach the LANIS server. Your Pipeline changes are still on this screen. Do NOT reload or close this tab. Check your internet connection, then click Save."}
            </span>
            <span className="pipeline-load-error-actions">
              {pipelineSaveState === "auth" && (
                <button className="button" onClick={logInInNewTab}>
                  Log In (New Tab)
                </button>
              )}
              <button className="button" onClick={savePendingPipeline}>
                Save
              </button>
            </span>
          </div>
        )}
        {!companyProfile.subscription_active && (
          <div className="error-messages">
            <p>
              Your subscription has expired.{" "}
              {companyProfile.normal_subscription &&
                (accountProfile.company_admin_role ? (
                  <>
                    Please go to{" "}
                    <Link to="/subscribe" className="button">
                      Subscribe
                    </Link>{" "}
                    to renew your subscription.
                  </>
                ) : (
                  "Please contact your administrator."
                ))}
              {!companyProfile.normal_subscription &&
                "Please contact customer support."}
            </p>
          </div>
        )}
        {(lockRiskDocs.length > 0 || coeRiskDocs.length > 0) && (
          <div className="pipeline-risk-bar">
            <span className="risk-bar-icon">⚠</span>
            {lockRiskDocs.length > 0 && (
              <button
                className={class_list(
                  "risk-bar-item",
                  "risk-bar-lock",
                  riskFilter === "lock" && "active"
                )}
                onClick={() =>
                  setRiskFilter(riskFilter === "lock" ? null : "lock")
                }
              >
                {lockRiskDocs.length} lock
                {lockRiskDocs.length > 1 ? "s" : ""} expiring soon
              </button>
            )}
            {lockRiskDocs.length > 0 && coeRiskDocs.length > 0 && (
              <span className="risk-bar-sep">·</span>
            )}
            {coeRiskDocs.length > 0 && (
              <button
                className={class_list(
                  "risk-bar-item",
                  "risk-bar-coe",
                  riskFilter === "coe" && "active"
                )}
                onClick={() =>
                  setRiskFilter(riskFilter === "coe" ? null : "coe")
                }
              >
                {coeRiskDocs.length} unlocked near COE
              </button>
            )}
            {riskFilter && (
              <button
                className="risk-bar-clear"
                onClick={() => setRiskFilter(null)}
              >
                Show all
              </button>
            )}
          </div>
        )}
        <div className="pipeline-section-nav">
          <div
            className={class_list(
              "pipeline-section-picker",
              "time-sensitive",
              currentSection === "TIME_SENSITIVE" && "active"
            )}
            onClick={() => setCurrentSection("TIME_SENSITIVE")}
          >
            Time Sensitive Pipeline
            <span className="pipeline-section-count">
              {timeSensitivePipeline.length}
            </span>
          </div>
          <div
            className={class_list(
              "pipeline-section-picker",
              currentSection === PURCHASE && "active"
            )}
            onClick={() => setCurrentSection(PURCHASE)}
          >
            Purchase Pipeline
            <span className="pipeline-section-count">
              {purchasePipeline.length}
            </span>
          </div>
          <div
            className={class_list(
              "pipeline-section-picker",
              currentSection === REFINANCE && "active"
            )}
            onClick={() => setCurrentSection(REFINANCE)}
          >
            Refinance Pipeline
            <span className="pipeline-section-count">
              {refinancePipeline.length}
            </span>
          </div>
        </div>
        <div>{renderFilter()}</div>
        <div className="pipeline-list">
          {currentSection === "TIME_SENSITIVE" &&
            renderList(timeSensitivePipeline)}
          {currentSection === PURCHASE && renderList(purchasePipeline)}
          {currentSection === REFINANCE && renderList(refinancePipeline)}
        </div>
        {showCreateModal && (
          <div className="create-name-modal-bg" onClick={hideCreateNameModal}>
            <div
              className="create-name-modal"
              onClick={e => e.stopPropagation()}
            >
              <TextField
                name={
                  fnmImporting
                    ? `${
                        importFormat === "fnm34"
                          ? "Fannie Mae 3.4"
                          : "Fannie Mae 3.2"
                      } – ${DOCUMENT_TYPES[documentType]} Document Name`
                    : `${DOCUMENT_TYPES[documentType]} Document Name`
                }
                fieldName="documentName"
                value={documentName}
                onChange={value => setDocumentName(value.documentName)}
              />
              { fnmImporting && (
                  <>
                    <div className="input-field-container import-file-field">
                      <label htmlFor="file">File</label>
                      <input
                          id="file"
                          className="input-field"
                          type="file"
                          accept={importFormat === "fnm34" ? ".xml" : ".fnm,.txt"}
                          ref={fileInputEl}
                          onChange={onImportFileChange}
                      />
                    </div>
                    <ErrorMessages messages={errorMessages} />
                    {importDocument &&
                      importDocument.accountSummaries &&
                      Object.keys(importDocument.accountSummaries).length > 0 && (
                        <div className="import-debt-filters">
                          <div className="import-debt-filters-label">
                            Debt accounts from the credit report
                          </div>
                          <CheckboxField
                            id="ignore_zero_balance"
                            name="Ignore accounts with a zero balance"
                            checked={ignoreZeroBalance}
                            fieldName="checked"
                            onChange={({ checked }) =>
                              setIgnoreZeroBalance(checked)
                            }
                          />
                          <CheckboxField
                            id="ignore_zero_payment"
                            name="Ignore accounts with a zero monthly payment"
                            checked={ignoreZeroPayment}
                            fieldName="checked"
                            onChange={({ checked }) =>
                              setIgnoreZeroPayment(checked)
                            }
                          />
                          <div className="import-debt-filters-hint">
                            {(() => {
                              const opts = {
                                ignoreZeroBalance,
                                ignoreZeroPayment
                              };
                              const dropped = countFilteredDebts(
                                importDocument,
                                opts
                              );
                              const total = Object.keys(
                                importDocument.accountSummaries
                              ).length;
                              return dropped > 0
                                ? `${dropped} of ${total} accounts will be left off. Deferred loans (e.g. student loans) may report $0 but can still count toward DTI — your choice.`
                                : `All ${total} accounts will be imported.`;
                            })()}
                          </div>
                        </div>
                      )}
                    <button
                        className="button"
                        disabled={!documentName || !importDocument}
                        onClick={() =>
                          createDocument(
                            applyDebtImportFilter(importDocument, {
                              ignoreZeroBalance,
                              ignoreZeroPayment
                            })
                          )
                        }
                    >
                      Create
                    </button>
                  </>
              ) }
              { !fnmImporting && (
                  <button
                      className="button"
                      disabled={!documentName}
                      onClick={() => createDocument()}
                  >
                    Create
                  </button>
              ) }
              <button className="button" onClick={hideCreateNameModal}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {copyDialogDoc && (
          <div className="create-name-modal-bg" onClick={closeCopyDialog}>
            <div
              className="create-name-modal"
              onClick={e => e.stopPropagation()}
            >
              <TextField
                name={`${DOCUMENT_TYPES[copyDialogDoc.type]} Document Name`}
                fieldName="copyName"
                value={copyName}
                onChange={value => setCopyName(value.copyName)}
              />
              <SelectField
                name="Keep all Dates and Lock Information?"
                fieldName="copyKeepDates"
                selectOptions={YES_NO}
                value={copyKeepDates}
                onChange={value => setCopyKeepDates(value.copyKeepDates)}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  lineHeight: "1.5",
                  textAlign: "left",
                  margin: "8px 0 4px"
                }}
              >
                Copies the entire loan file — borrowers, all loans, closing
                costs, REO, debts, income and reserves. Status and Loan Number
                always start fresh.
              </p>
              {copyError && <ErrorMessages messages={[copyError]} />}
              <button
                className="button"
                disabled={!copyName || copyBusy}
                onClick={copyDocument}
              >
                {copyBusy ? "Copying…" : "Create"}
              </button>
              <button className="button" onClick={closeCopyDialog}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {archiveDialogDoc && (
          <div
            className="create-name-modal-bg"
            onClick={() => setArchiveDialogDoc(null)}
          >
            <div
              className="create-name-modal"
              onClick={e => e.stopPropagation()}
            >
              <p style={{ marginBottom: "20px", lineHeight: "1.5" }}>
                Do you really want to archive this loan?{" "}
                <b>{archiveDialogDoc.name}</b>
                <br />
                <span style={{ fontSize: "12px", color: "#666" }}>
                  It will be hidden from the pipeline but not deleted — you can
                  restore it anytime with Show Archived.
                </span>
              </p>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  className="button"
                  onClick={() => {
                    onPipelineDataChange(
                      { archived: true },
                      archiveDialogDoc.id,
                      true
                    );
                    setArchiveDialogDoc(null);
                  }}
                >
                  Yes, Archive Loan File
                </button>
                <button
                  className="button"
                  onClick={() => setArchiveDialogDoc(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </StandardTemplate>
  );
}
